import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../shared/components/layout/Button';
import { onboardingService, OnboardingStep } from '../services/onboardingService';
import { Spinner } from '../../../shared/components/common/Spinner';

interface OnboardingProps {
    onComplete: () => Promise<void>;
}

export const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
    const [currentStep, setCurrentStep] = React.useState(0);
    const [steps, setSteps] = useState<OnboardingStep[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCompleting, setIsCompleting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchOnboardingSteps = async () => {
            try {
                const data = await onboardingService.getOnboardingSteps();
                setSteps(data);
                setIsLoading(false);
            } catch (err) {
                setError('Failed to load onboarding content');
                setIsLoading(false);
            }
        };

        fetchOnboardingSteps();
    }, []);

    const isLastStep = currentStep === steps.length - 1;

    const handleNext = async () => {
        if (isLastStep) {
            console.log('[Onboarding] Last step, starting completion...');
            setIsCompleting(true);
            try {
                await onComplete();
                console.log('[Onboarding] Completion callback finished');
            } catch (err) {
                console.error('[Onboarding] Error during completion:', err);
                setError('Failed to complete onboarding');
            } finally {
                setIsCompleting(false);
            }
        } else {
            setCurrentStep(prev => prev + 1);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Spinner variant="border" size="lg" className="text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-red-600">{error}</div>
            </div>
        );
    }

    const currentStepData = steps[currentStep];

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
            <div className="max-w-2xl w-full space-y-8 text-center">
                {/* Above Headline */}

                {/* Image */}
                <div className="w-full aspect-video bg-gray-50 rounded-lg overflow-hidden">
                    <img
                        src={currentStepData.image}
                        alt={currentStepData.headline}
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Progress Indicator */}
                <div className="flex justify-center space-x-2">
                    {steps.map((_, index) => (
                        <div
                            key={index}
                            className={`h-2 w-2 rounded-full ${
                                index === currentStep ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                        />
                    ))}
                </div>

                {/* Content */}
                <div className="space-y-4">
                    <div className="text-blue-600 font-medium text-lg">
                        {currentStepData.aboveHeadline}
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900">
                        {currentStepData.headline}
                    </h1>
                    <p className="text-xl text-gray-600">
                        {currentStepData.description}
                    </p>
                </div>

                {/* Button */}
                <div className="pt-8">
                    <Button
                        onClick={handleNext}
                        disabled={isCompleting}
                        className="w-full sm:w-auto px-8 py-3"
                    >
                        {isLastStep ? (isCompleting ? 'Completing...' : 'Finish') : 'Next'}
                    </Button>
                </div>
            </div>
        </div>
    );
}; 